// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import Notify from "@eisinc/App/Components/Notify";
import RequestCerificationsModal from "@eisinc/App/Components/RequestCerificationsModal/RequestCerificationsModal";
import { parseBoolean } from "@eisinc/App/Utilities/helpers";
import Button, { ButtonPresentationProps } from "@eisinc/MobiusOverrides/Button";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCartState, getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const enum fields {
    heading = "heading",
    description = "description",
    buttonText = "buttonText",
    notifyType = "notifyType",
}

interface RequestCertificationsMessageProps extends WidgetProps {
    fields: {
        [fields.heading]: string;
        [fields.description]: string;
        [fields.buttonText]: string;
        [fields.notifyType]: "warning" | "danger" | "info";
    };
}

interface RequestCertificationsMessageStyles {
    notify?: InjectableCss;
    heading?: TypographyPresentationProps;
    description?: TypographyPresentationProps;
    button?: ButtonPresentationProps;
}

const requestCertificationsMessageStyles: RequestCertificationsMessageStyles = {
    notify: {
        css: css`
            .notify-content-wrapper {
                flex-direction: column;
                align-items: flex-start;
            }
        `,
    },
    heading: {
        variant: "h6",
        forwardAs: "p",
    },
    description: {
        css: css`
            margin-bottom: 8px;
        `,
    },
    button: {
        mergeCss: true,
        variant: "link" as any,
    },
};

const mapStateToProps = (state: ApplicationState) => {
    const { cartId } = state.pages.checkoutReviewAndSubmit;
    const cartState = cartId ? getCartState(state, cartId) : getCurrentCartState(state);
    return { cart: cartState.value };
};

type Props = RequestCertificationsMessageProps & ReturnType<typeof mapStateToProps>;

const styles = requestCertificationsMessageStyles;

const RequestCertificationsMessage: React.FC<Props> = ({ fields, cart }) => {
    const [isModalOpen, setIsModalOpen] = React.useState(false);

    const cartHasCertText = parseBoolean(cart?.properties["containsCertText"]);

    const cartAlreadyHasCertifications = cart?.cartLines?.some(
        line =>
            parseBoolean(line.properties["eis/GCGCertification"]) || parseBoolean(line.properties["mfrCertification"]),
    );

    const showRequestCertificationsMessage = cart && cartHasCertText && !cartAlreadyHasCertifications;

    if (!showRequestCertificationsMessage) {
        return null;
    }

    return (
        <>
            <Notify
                {...styles.notify}
                messageType={fields.notifyType}
                body={
                    <>
                        {fields.heading && <Typography {...styles.heading}>{fields.heading}</Typography>}
                        {fields.description && <Typography {...styles.description}>{fields.description}</Typography>}
                        <Button {...styles.button} onClick={() => setIsModalOpen(true)}>
                            {fields.buttonText}
                        </Button>
                    </>
                }
            />
            <RequestCerificationsModal
                cart={cart}
                isOpen={isModalOpen}
                closeModalHandler={() => setIsModalOpen(false)}
            />
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, null)(RequestCertificationsMessage),
    definition: {
        displayName: "Request Certifications Message",
        group: "Checkout - Review & Submit",
        allowedContexts: ["CheckoutReviewAndSubmitPage"],
        fieldDefinitions: [
            {
                name: fields.heading,
                displayName: "Heading",
                editorTemplate: "TextField",
                defaultValue: "YOUR ORDER MAY BE DELAYED",
                fieldType: "Translatable",
            },
            {
                name: fields.description,
                displayName: "Description",
                editorTemplate: "MultilineTextField",
                defaultValue:
                    "To avoid order delays, please remove certification requests from Product or Order Notes. If you need manufacturer or EIS certificates, ",
                fieldType: "Translatable",
            },
            {
                name: fields.buttonText,
                displayName: "Button Text",
                editorTemplate: "TextField",
                defaultValue: "Click Here To Request Certificates",
                fieldType: "Translatable",
            },
            {
                name: fields.notifyType,
                displayName: "Notify Type",
                editorTemplate: "DropDownField",
                defaultValue: "warning",
                fieldType: "General",
                options: [
                    { displayName: "Info", value: "info" },
                    { displayName: "Warning", value: "warning" },
                    { displayName: "Danger", value: "danger" },
                ],
                hideEmptyOption: true,
            },
        ],
    },
};

export default widgetModule;
